// De webshop-opties van een widget in de widget zelf (golf 1C, Martijn 10-10-2026).
//
// De api (GET api/Webshop/GetWebshopWidgetOpties) geeft altijd een volledig ingevuld geheel; komt er niets (oude api, geen
// webshopwidget, netwerkfout), dan geldt de standaard hieronder: raster, 3 kolommen op de computer, 1 op de telefoon,
// vierkante foto's, alles tonen behalve voorraad, tabs, zoekbalk, geen minimum. Zo ziet een webshop er zonder opties uit
// zoals hij er altijd uitzag.
//
// Hier staan alleen pure functies (geen React), zodat de proeven ze los kunnen nakijken.

import { ALLERGEENOPTIES } from './allergeennamen';
import { hoekMaat } from "./hoek";
import { isVoorbeeld } from "./weergave";

export const STANDAARD_OPTIES = Object.freeze({
    soort: 'horeca',
    weergave: 'raster',
    kolommenComputer: 3,
    kolommenTelefoon: 1,
    fotovorm: 'vierkant',
    toonBeschrijving: true,
    toonAllergenen: true,
    toonVoorraad: false,
    toonPopulair: true,
    categorieWeergave: 'tabs',
    toonZoekbalk: true,
    sortering: 'standaard',
    minimaalBestelbedrag: 0,
    opmerkingPerProduct: true,
    opmerkingPerBestelling: true,
    levertijd: null,
    bezorgkosten: 0,
    gratisVanaf: 0,
    // Bezorgmethodes (13-10-2026): null = de server zei niets (oud); dan volgen ze uit de webshopinstellingen (lib/bezorgmethodes).
    methodes: null,
    levertijdVan: null,
    levertijdTot: null,
    verzendGrens: null,
    bereidingstijd: null,
    verzendtarieven: [],
    bezorgtijden: [],
    besteltijdGrens: null,
});

const binnen = (waarde, lijst, standaard) => (lijst.includes(waarde) ? waarde : standaard);
const getal = (waarde, laag, hoog, standaard) => {
    const n = Number(waarde);
    return Number.isFinite(n) ? Math.min(hoog, Math.max(laag, Math.round(n))) : standaard;
};
const bedrag = (waarde) => {
    const n = Number(waarde);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
};
const jaNee = (waarde, standaard) => (typeof waarde === 'boolean' ? waarde : standaard);

/** Een volledig, begrensd optiesobject, wat de api ook stuurt. */
export function normaliseerOpties(ruw) {
    const o = ruw && typeof ruw === 'object' ? ruw : {};
    const s = STANDAARD_OPTIES;
    return {
        soort: binnen(o.soort, ['horeca', 'retail'], s.soort),
        weergave: binnen(o.weergave, ['raster', 'lijst', 'compact'], s.weergave),
        kolommenComputer: getal(o.kolommenComputer, 2, 6, s.kolommenComputer),
        kolommenTelefoon: getal(o.kolommenTelefoon, 1, 2, s.kolommenTelefoon),
        fotovorm: binnen(o.fotovorm, ['vierkant', 'rond', 'breed', 'geen'], s.fotovorm),
        toonBeschrijving: jaNee(o.toonBeschrijving, s.toonBeschrijving),
        toonAllergenen: jaNee(o.toonAllergenen, s.toonAllergenen),
        toonVoorraad: jaNee(o.toonVoorraad, s.toonVoorraad),
        toonPopulair: jaNee(o.toonPopulair, s.toonPopulair),
        categorieWeergave: binnen(o.categorieWeergave, ['tabs', 'zijbalk'], s.categorieWeergave),
        toonZoekbalk: jaNee(o.toonZoekbalk, s.toonZoekbalk),
        sortering: binnen(o.sortering, ['standaard', 'naam', 'prijs-op', 'prijs-af', 'populair'], s.sortering),
        minimaalBestelbedrag: bedrag(o.minimaalBestelbedrag),
        opmerkingPerProduct: jaNee(o.opmerkingPerProduct, s.opmerkingPerProduct),
        opmerkingPerBestelling: jaNee(o.opmerkingPerBestelling, s.opmerkingPerBestelling),
        levertijd: typeof o.levertijd === 'string' && o.levertijd.trim() ? o.levertijd.trim() : null,
        bezorgkosten: bedrag(o.bezorgkosten),
        gratisVanaf: bedrag(o.gratisVanaf),
        methodes: ['afhalen', 'bezorgen', 'verzenden', 'hierEten'].some((k) => typeof o[k] === 'boolean')
            ? { afhalen: o.afhalen === true, bezorgen: o.bezorgen === true, verzenden: o.verzenden === true, hierEten: o.hierEten === true }
            : null,
        levertijdVan: Number.isFinite(Number(o.levertijdVan)) && o.levertijdVan !== null ? getal(o.levertijdVan, 0, 60, null) : null,
        levertijdTot: Number.isFinite(Number(o.levertijdTot)) && o.levertijdTot !== null ? getal(o.levertijdTot, 0, 60, null) : null,
        verzendGrens: typeof o.verzendGrens === 'string' && /^\d{2}:\d{2}$/.test(o.verzendGrens) ? o.verzendGrens : null,
        bereidingstijd: Number(o.bereidingstijd) > 0 ? Math.round(Number(o.bereidingstijd)) : null,
        verzendtarieven: Array.isArray(o.verzendtarieven)
            ? o.verzendtarieven.filter((t) => t && /^[A-Za-z]{2}$/.test(t.landcode || '')).map((t) => ({
                landcode: String(t.landcode).toUpperCase(), kosten: bedrag(t.kosten), gratisVanaf: bedrag(t.gratisVanaf) }))
            : [],
        bezorgtijden: Array.isArray(o.bezorgtijden) ? o.bezorgtijden : [],
        besteltijdGrens: typeof o.besteltijdGrens === 'string' ? o.besteltijdGrens : null,
    };
}

/**
 * Het aantal kolommen per schermbreedte (MUI-breekpunten). Een telefoon (xs) volgt kolommenTelefoon, een tablet (sm) hooguit 2
 * meer dan de telefoon, een kleine laptop (md) hooguit 3, en vanaf lg de keuze voor de computer. De lijst en compact: op de
 * telefoon altijd 1 (een regel per product), op de computer hooguit 2.
 */
export function kolommen(opties) {
    const o = opties || STANDAARD_OPTIES;
    if (o.weergave !== 'raster') {
        const breed = Math.min(2, o.kolommenComputer);
        return { xs: 1, sm: 1, md: breed, lg: breed };
    }
    return {
        xs: o.kolommenTelefoon,
        sm: Math.min(o.kolommenComputer, Math.max(o.kolommenTelefoon, 2)),
        md: Math.min(o.kolommenComputer, 3),
        lg: o.kolommenComputer,
    };
}

/** De verhouding en ronding van de foto, of null voor geen foto. */
export function fotostijl(opties) {
    switch ((opties || STANDAARD_OPTIES).fotovorm) {
        case 'geen': return null;
        case 'rond': return { aspectRatio: '1 / 1', borderRadius: '50%' };
        case 'breed': return { aspectRatio: '16 / 9', borderRadius: hoekMaat(8) };
        default: return { aspectRatio: '1 / 1', borderRadius: hoekMaat(8) };
    }
}

const prijsVan = (p) => Number(p?.price) || 0;
const naamVan = (p) => String(p?.displayname || p?.displayName || '');

/** De producten in de gekozen volgorde. "standaard" laat de volgorde van het dashboard staan. Verandert de lijst niet. */
export function sorteer(producten, sortering) {
    const lijst = Array.isArray(producten) ? [...producten] : [];
    switch (sortering) {
        case 'naam': return lijst.sort((a, b) => naamVan(a).localeCompare(naamVan(b), 'nl', { sensitivity: 'base' }));
        case 'prijs-op': return lijst.sort((a, b) => prijsVan(a) - prijsVan(b));
        case 'prijs-af': return lijst.sort((a, b) => prijsVan(b) - prijsVan(a));
        case 'populair': return lijst.sort((a, b) => (Number(b?.soldstock) || 0) - (Number(a?.soldstock) || 0));
        default: return lijst;
    }
}

/** De best verkochte producten van deze lijst (hooguit 3, alleen als er echt iets verkocht is). */
export function populaireProducten(producten, aantal = 3) {
    return new Set(
        (Array.isArray(producten) ? producten : [])
            .filter((p) => (Number(p?.soldstock) || 0) > 0)
            .sort((a, b) => (Number(b?.soldstock) || 0) - (Number(a?.soldstock) || 0))
            .slice(0, aantal)
            .map((p) => p.idproduct)
    );
}

/**
 * Wat er aan een product ontbreekt, voor de markeringen in de bewerkmodus: "beschrijving" en (alleen bij horeca) "allergenen".
 */
export function ontbreekt(product, soort = 'horeca') {
    const uit = [];
    if (!String(product?.info || '').trim()) uit.push('beschrijving');
    if (soort === 'horeca' && !(Array.isArray(product?.allergenen) && product.allergenen.length > 0)) uit.push('allergenen');
    return uit;
}

/**
 * Haalt de bestelling het minimum niet? Het minimum telt over de producten (zonder bezorgkosten) en niet bij hier eten.
 * Geeft het bedrag dat nog ontbreekt in centen, of 0.
 */
export function tekortVoorMinimum(productenInCenten, minimaalBestelbedrag, bezorgtype) {
    if (bezorgtype === 'DineIn') return 0;
    const minimum = Math.round((Number(minimaalBestelbedrag) || 0) * 100);
    if (minimum <= 0) return 0;
    const totaal = Math.round(Number(productenInCenten) || 0);
    return totaal >= minimum ? 0 : minimum - totaal;
}

/** De naam van een allergeen in de taal van de widget (anders Nederlands, anders Engels). */
export function allergeenNaam(id, taal = 'nl') {
    const optie = ALLERGEENOPTIES.find((o) => o.id === Number(id));
    if (!optie) return null;
    return optie.namen[taal] || optie.namen.nl || optie.namen.en || optie.sleutel;
}

/** De allergenen van een product die de gast ziet: alleen echte allergenen en kruisbesmetting, geen voedingskenmerken. */
export function allergenenVoorGast(ids) {
    const set = new Set((Array.isArray(ids) ? ids : []).map(Number));
    return ALLERGEENOPTIES.filter((o) => set.has(o.id) && o.groep !== 'voeding');
}

/** De voedingskenmerken (vegan, glutenvrij...) van een product, als korte labels. */
export function voedingskenmerken(ids) {
    const set = new Set((Array.isArray(ids) ? ids : []).map(Number));
    return ALLERGEENOPTIES.filter((o) => set.has(o.id) && o.groep === 'voeding');
}

/** Tekstvarianten per soort: bezorgen heet bij retail verzenden, afhalen is afhalen in de winkel. */
export function bezorgwoorden(soort) {
    return soort === 'retail'
        ? { bezorgen: 'Verzenden', afhalen: 'Afhalen in de winkel', bezorgkosten: 'Verzendkosten' }
        : { bezorgen: 'Bezorgen', afhalen: 'Afhalen', bezorgkosten: 'Bezorgkosten' };
}

/**
 * Toont de widget de voorpagina (de kaarten Afhalen, Bezorgen, Hier eten, Verzenden)? Alleen als de zaak precies een manier aan
 * heeft kan hij uit (webshopSettings.showStartScreen === false). In het live voorbeeld van het dashboard bepaalt de
 * queryparameter voorpagina=0|1 de stand, nog voor Opslaan.
 */
export function toonVoorpagina(instellingen, zoek = "") {
  if (isVoorbeeld(zoek)) {
    const gekozen = new URLSearchParams(zoek || "").get("voorpagina");
    if (gekozen === "0") return false;
    if (gekozen === "1") return true;
  }
  return instellingen?.showStartScreen !== false;
}

/** De praktische bovengrens per artikel als de zaak geen limiet heeft ingesteld (tegen typefouten). */
export const PRAKTISCHE_BOVENGRENS = 999;

/** Hoeveel van hetzelfde artikel: leeg of 0 = geen limiet (hooguit 999), anders de instelling van de zaak. */
export function maxPerArtikel(instellingen) {
  const n = Math.floor(Number(instellingen?.maxQuantityOfEachProduct));
  return n > 0 ? Math.min(n, PRAKTISCHE_BOVENGRENS) : PRAKTISCHE_BOVENGRENS;
}

/** Hoeveel artikelen in een bestelling: leeg of 0 = geen limiet (Infinity). */
export function maxTotaal(instellingen) {
  const n = Math.floor(Number(instellingen?.maxQuantityOfTotalProducts));
  return n > 0 ? n : Infinity;
}

/**
 * Hoeveel er van dit artikel nog bij mag: het kleinste van de grens per artikel en wat er in de bestelling nog over is
 * (zonder dit artikel zelf). `andere` = het aantal van alle andere artikelen in de winkelwagen.
 */
export function toegestaanAantal(instellingen, andere = 0) {
  return Math.max(0, Math.min(maxPerArtikel(instellingen), maxTotaal(instellingen) - Math.max(0, andere)));
}

/** Het aantal artikelen van alle regels in de winkelwagen behalve `idproduct` (een regel met een hoeveelheid per eenheid telt 1). */
export function aantalAndere(producten, aantallen, idproduct, isEenheid = () => false) {
  return (Array.isArray(producten) ? producten : []).reduce(
    (som, p) => (p?.idproduct == idproduct ? som : som + (isEenheid(p) ? 1 : Number(aantallen?.[p?.idproduct]) || 0)),
    0
  );
}
